'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { FillScreen } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { stacktraceNS, useTranslation } from '@/lib/i18n';
import { useSuspenseQueryOperationPackageInstance } from '../../-queries';
import { AdjustProductScopeOperation } from './-components/AdjustProductScopeOperation';
import { OrganizeValuesOperation } from './-components/OrganizeValuesOperation';
import { resolveDefaultAdjustScopeTask } from './-utils/resolveAdjustScopeTask';
import { resolveDefaultOrganizeValuesTask } from './-utils/resolveOrganizeValuesTask';
export const Route = createFileRoute('/_stacktrace/product-planning-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/_layout/data/')({
  component: RouteComponent
});

/**
 * PP オペレーションコア インスタンス詳細「データ」タブ。
 *
 * PP の 2 パッケージが Step2 をデータタブで遂行する（タスクコードは
 * グローバル一意のため、steps から解決したタスクで出し分ける）:
 * - プロダクトスコープ棚卸し: `AdjustScope`（スコープツリー表示 + 完了）
 * - 価値の棚卸し: `OrganizeValues`（価値一覧表示 + 完了）
 *
 * Step1（アサイン）は詳細タブのタスクカード内でインライン完了するため
 * データタブでは扱わない。完了後は detail タブへ戻す（QA テスト設計の
 * TestSpecDesignDataTab と同型）。
 */
function RouteComponent() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'productPlanningCore.productScopeReconciliation.instance.data'
  });
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const navigate = Route.useNavigate();
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  const steps = instance.operationSteps;
  const adjustTask = useMemo(() => resolveDefaultAdjustScopeTask(steps), [steps]);
  const organizeValuesTask = useMemo(() => resolveDefaultOrganizeValuesTask(steps), [steps]);
  const handleCompleted = useCallback(() => {
    navigate({
      to: '/product-planning-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/detail',
      params
    });
  }, [navigate, params]);
  if (organizeValuesTask) {
    return <FillScreen>
        <OrganizeValuesOperation instanceId={instanceId} taskId={organizeValuesTask.task.id} isCompleted={organizeValuesTask.task.status === 'Completed'} onCompleted={handleCompleted} />
      </FillScreen>;
  }
  if (adjustTask) {
    return <FillScreen>
        <AdjustProductScopeOperation instanceId={instanceId} taskId={adjustTask.task.id} isCompleted={adjustTask.task.status === 'Completed'} onCompleted={handleCompleted} />
      </FillScreen>;
  }
  return <FillScreen className="flex items-center justify-center">
      <Text variant="label" className="text-neutral-muted-on-fill">
        {t('noTask')}
      </Text>
    </FillScreen>;
}